import {
  type SceneTransition,
  isOverlapKind,
  clampTransitionDuration,
} from './types';

export interface SceneLike { startTime: number; endTime: number }

export interface SceneLayoutBlock {
  from: number;
  durationInFrames: number;
  trimBefore: number;
  trimAfter: number;
}

export interface CutWindow {
  index: number;
  transition: SceneTransition;
  startFrame: number;
  endFrame: number;
  boundaryFrame: number;
  overlapFrames: number;
}

export interface SceneLayout {
  blocks: SceneLayoutBlock[];
  cuts: CutWindow[];
  totalDurationInFrames: number;
}

/** Overlap consumed by a cut, in frames. 0 for transform kinds. Capped at
 *  half the shorter neighbor so a transition can never swallow a scene. */
export function overlapFramesForCut(
  t: SceneTransition,
  durAFrames: number,
  durBFrames: number,
  fps: number,
): number {
  if (!isOverlapKind(t.kind)) return 0;
  const want = Math.round(clampTransitionDuration(t.durationSec) * fps);
  const cap = Math.floor(Math.min(durAFrames, durBFrames) / 2);
  return Math.max(0, Math.min(want, cap));
}

/** Derive cut windows for blocks already placed on the output timeline.
 *  Shared by buildSceneLayout and cutsFromClipIntervals. */
export function deriveCutWindows(
  blocks: Array<Pick<SceneLayoutBlock, 'from' | 'durationInFrames'>>,
  transitions: SceneTransition[],
  fps: number,
): CutWindow[] {
  const byIndex = new Map(transitions.map((t) => [t.afterSceneIndex, t]));
  const cuts: CutWindow[] = [];
  for (let i = 0; i < blocks.length - 1; i++) {
    const t = byIndex.get(i);
    if (!t) continue;
    const a = blocks[i];
    const b = blocks[i + 1];
    if (a.durationInFrames <= 0 || b.durationInFrames <= 0) continue;
    if (isOverlapKind(t.kind)) {
      const overlap = a.from + a.durationInFrames - b.from;
      if (overlap <= 0) continue; // back-to-back placement (e.g. old backend)
      cuts.push({
        index: i,
        transition: t,
        startFrame: b.from,
        endFrame: b.from + overlap,
        boundaryFrame: b.from,
        overlapFrames: overlap,
      });
    } else {
      const want = Math.round(clampTransitionDuration(t.durationSec) * fps);
      const halfA = Math.min(Math.ceil(want / 2), Math.floor(a.durationInFrames / 2));
      const halfB = Math.min(Math.floor(want / 2), Math.floor(b.durationInFrames / 2));
      if (halfA + halfB <= 0) continue;
      cuts.push({
        index: i,
        transition: t,
        startFrame: b.from - halfA,
        endFrame: b.from + halfB,
        boundaryFrame: b.from,
        overlapFrames: 0,
      });
    }
  }
  return cuts;
}

/** THE single source of truth for scene placement with transitions. With no
 *  transitions the blocks are byte-identical to the legacy back-to-back
 *  cumulative loop (ClipVideoSequences/ProjectPreview/backend). The backend
 *  mirrors the offset math in
 *  ../autoclipper-backend/src/lib/sceneTransitions.js. */
export function buildSceneLayout(
  { scenes, transitions, fps }:
  { scenes: SceneLike[]; transitions: SceneTransition[]; fps: number },
): SceneLayout {
  const byIndex = new Map(transitions.map((t) => [t.afterSceneIndex, t]));
  const dims = scenes.map((s) => {
    const segStart = Math.max(0, s.startTime);
    const segEnd = Math.max(segStart, s.endTime);
    const trimBefore = Math.round(segStart * fps);
    const trimAfter = Math.round(segEnd * fps);
    return { trimBefore, trimAfter, dur: Math.max(0, trimAfter - trimBefore) };
  });

  const blocks: SceneLayoutBlock[] = [];
  let cursor = 0;
  dims.forEach((d, i) => {
    if (i > 0) {
      const t = byIndex.get(i - 1);
      if (t) cursor -= overlapFramesForCut(t, dims[i - 1].dur, d.dur, fps);
    }
    blocks.push({
      from: cursor,
      durationInFrames: d.dur,
      trimBefore: d.trimBefore,
      trimAfter: d.trimAfter,
    });
    cursor += d.dur;
  });

  const last = blocks[blocks.length - 1];
  return {
    blocks,
    cuts: deriveCutWindows(blocks, transitions, fps),
    totalDurationInFrames: last ? last.from + last.durationInFrames : 0,
  };
}
